import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'

interface TaxTemplate {
  id: string
  name: string
  description?: string
  isActive: boolean
  items: Array<{
    id: string
    taxRate: {
      id: string
      name: string
      rate: string
      taxType: string
    }
    sortOrder: number
  }>
}

interface TaxRate {
  id: string
  name: string
  rate: string
  taxType: string
}

const TaxTemplates: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission, loading: permissionsLoading } = usePermissions(user)
  const [templates, setTemplates] = useState<TaxTemplate[]>([])
  const [taxRates, setTaxRates] = useState<TaxRate[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<TaxTemplate | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true,
    taxRateIds: [] as string[],
  })

  useEffect(() => {
    fetchTemplates()
    fetchTaxRates()
  }, [])

  const fetchTemplates = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get('/api/erp/tax-templates')
      setTemplates(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching tax templates:', error)
      toast.error('Failed to load tax templates')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchTaxRates = async () => {
    try {
      const response = await axios.get('/api/erp/tax-rates', { params: { isActive: 'true' } })
      setTaxRates(response.data.data || [])
    } catch (error) {
      console.error('Error fetching tax rates:', error)
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name || formData.taxRateIds.length === 0) {
      toast.error('Please provide a name and at least one tax rate')
      return
    }

    try {
      await axios.post('/api/erp/tax-templates', formData)
      toast.success('Tax template created successfully!')
      setShowCreateModal(false)
      setFormData({
        name: '',
        description: '',
        isActive: true,
        taxRateIds: [],
      })
      fetchTemplates()
    } catch (error: any) {
      console.error('Error creating tax template:', error)
      toast.error(error.response?.data?.error || 'Failed to create tax template')
    }
  }

  const handleEdit = (template: TaxTemplate) => {
    setSelectedTemplate(template)
    setFormData({
      name: template.name,
      description: template.description || '',
      isActive: template.isActive,
      taxRateIds: template.items.map(item => item.taxRate.id),
    })
    setShowEditModal(true)
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedTemplate) return

    if (!formData.name || formData.taxRateIds.length === 0) {
      toast.error('Please provide a name and at least one tax rate')
      return
    }

    try {
      await axios.put(`/api/erp/tax-templates/${selectedTemplate.id}`, formData)
      toast.success('Tax template updated successfully!')
      setShowEditModal(false)
      setSelectedTemplate(null)
      fetchTemplates()
    } catch (error: any) {
      console.error('Error updating tax template:', error)
      toast.error(error.response?.data?.error || 'Failed to update tax template')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this tax template?')) return
    try {
      await axios.delete(`/api/erp/tax-templates/${id}`)
      toast.success('Tax template deleted successfully!')
      fetchTemplates()
    } catch (error: any) {
      console.error('Error deleting tax template:', error)
      toast.error(error.response?.data?.error || 'Failed to delete tax template')
    }
  }

  const toggleTaxRate = (taxRateId: string) => {
    setFormData(prev => ({
      ...prev,
      taxRateIds: prev.taxRateIds.includes(taxRateId)
        ? prev.taxRateIds.filter(id => id !== taxRateId)
        : [...prev.taxRateIds, taxRateId],
    }))
  }

  if (permissionsLoading) {
    return <div className="p-6">Loading...</div>
  }

  if (!hasPermission('erp_tax_template', 'read')) {
    return (
      <div className="p-6">
        <p className="text-red-800">You don't have permission to view tax templates.</p>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Tax Templates</h1>
          <PageHelp pageIdentifier="tax-templates" title="Tax Templates" />
        </div>
        {hasPermission('erp_tax_template', 'write') && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Add Tax Template
          </button>
        )}
      </div>

      {isLoading ? (
        <p className="text-gray-600">Loading tax templates...</p>
      ) : templates.length === 0 ? (
        <p className="text-gray-600">No tax templates found. Add your first tax template.</p>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tax Rates</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {templates.map((template) => (
                <tr key={template.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{template.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{template.description || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {template.items.map(item => item.taxRate.name).join(', ')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${template.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {template.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {hasPermission('erp_tax_template', 'write') && (
                      <>
                        <button
                          onClick={() => handleEdit(template)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                        >
                          Edit
                        </button>
                      </>
                    )}
                    {hasPermission('erp_tax_template', 'delete') && (
                      <button
                        onClick={() => handleDelete(template.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Add Tax Template</h2>
            <form onSubmit={handleCreate}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                ></textarea>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tax Rates <span className="text-red-500">*</span>
                </label>
                <div className="border border-gray-300 rounded-md p-3 max-h-60 overflow-y-auto">
                  {taxRates.length === 0 ? (
                    <p className="text-sm text-gray-500">No active tax rates available</p>
                  ) : (
                    taxRates.map((taxRate) => (
                      <label key={taxRate.id} className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          checked={formData.taxRateIds.includes(taxRate.id)}
                          onChange={() => toggleTaxRate(taxRate.id)}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded mr-2"
                        />
                        <span className="text-sm text-gray-700">
                          {taxRate.name} ({taxRate.rate}% {taxRate.taxType})
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded mr-2"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Add Tax Template
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedTemplate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Edit Tax Template</h2>
            <form onSubmit={handleUpdate}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                ></textarea>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tax Rates <span className="text-red-500">*</span>
                </label>
                <div className="border border-gray-300 rounded-md p-3 max-h-60 overflow-y-auto">
                  {taxRates.map((taxRate) => (
                    <label key={taxRate.id} className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        checked={formData.taxRateIds.includes(taxRate.id)}
                        onChange={() => toggleTaxRate(taxRate.id)}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded mr-2"
                      />
                      <span className="text-sm text-gray-700">
                        {taxRate.name} ({taxRate.rate}% {taxRate.taxType})
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded mr-2"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    setSelectedTemplate(null)
                  }}
                  className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Update Tax Template
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default TaxTemplates
